import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { supabase } from '../lib/api';

// ── shared styles ──────────────────────────────────────────────────────
const card = {
  background: 'var(--white, #fff)',
  border: '1px solid var(--grey-100)',
  borderRadius: 'var(--radius, 12px)',
  padding: 16,
  marginBottom: 16,
};
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--grey-300)',
  borderRadius: 'var(--radius-sm, 8px)',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
};

// ── maths helpers ──────────────────────────────────────────────────────
// Epley estimated 1-rep-max. Normalises different rep schemes onto one
// comparable number. Reps of 1 return the weight itself.
function e1rm(weight, reps) {
  if (weight == null || reps == null || reps < 1) return null;
  return weight * (1 + reps / 30);
}
function round1(n) {
  return Math.round(n * 10) / 10;
}

// Collapse all logged sets of one exercise into one point per session:
// the session's best e1RM, plus the set that produced it.
function sessionPoints(rows) {
  const byDate = new Map();
  for (const r of rows) {
    const est = e1rm(Number(r.weight), Number(r.reps));
    if (est == null) continue;
    const key = r.performed_on;
    const prev = byDate.get(key);
    if (!prev || est > prev.e1rm) {
      byDate.set(key, {
        date: key,
        e1rm: est,
        weight: Number(r.weight),
        reps: Number(r.reps),
      });
    }
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

// ── a tiny dependency-free SVG line chart ──────────────────────────────
function LineChart({ points, accessor, color = '#FF6200', unit = '', height = 160, lowerIsBetter = false }) {
  const vals = points.map(accessor).filter(v => v != null && !Number.isNaN(v));
  if (points.length === 0 || vals.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--grey-500)', fontSize: '0.85rem' }}>
        Not enough data yet.
      </div>
    );
  }

  const W = 320, H = height, padL = 38, padR = 12, padT = 14, padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) { min -= 1; max += 1; }
  const span = max - min;
  const pad = span * 0.1;
  min -= pad; max += pad;

  const n = points.length;
  const x = i => padL + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = v => padT + innerH - ((v - min) / (max - min)) * innerH;

  const drawn = points
    .map((p, i) => ({ i, v: accessor(p) }))
    .filter(d => d.v != null && !Number.isNaN(d.v));

  const path = drawn.map((d, k) => `${k === 0 ? 'M' : 'L'} ${x(d.i)} ${y(d.v)}`).join(' ');
  const areaPath =
    drawn.length > 1
      ? `${path} L ${x(drawn[drawn.length - 1].i)} ${padT + innerH} L ${x(drawn[0].i)} ${padT + innerH} Z`
      : '';

  const first = drawn[0].v;
  const last = drawn[drawn.length - 1].v;
  const delta = last - first;
  const improved = lowerIsBetter ? delta < 0 : delta > 0;
  const gridVals = [min + (max - min) * 0.0, min + (max - min) * 0.5, max - (max - min) * 0.0];

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        <defs>
          <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {gridVals.map((gv, k) => (
          <g key={k}>
            <line x1={padL} x2={W - padR} y1={y(gv)} y2={y(gv)} stroke="var(--grey-100)" strokeWidth="1" />
            <text x={4} y={y(gv) + 3} fontSize="9" fill="var(--grey-500)">{round1(gv)}</text>
          </g>
        ))}
        {areaPath && <path d={areaPath} fill={`url(#grad-${color.replace('#', '')})`} />}
        <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {drawn.map((d, k) => (
          <circle key={k} cx={x(d.i)} cy={y(d.v)} r={drawn.length <= 12 ? 3 : 2} fill={color} />
        ))}
        {/* first & last date labels */}
        <text x={x(drawn[0].i)} y={H - 8} fontSize="9" fill="var(--grey-500)" textAnchor="middle">
          {format(parseISO(points[drawn[0].i].date || points[drawn[0].i].scanned_on), 'd MMM')}
        </text>
        {drawn.length > 1 && (
          <text x={x(drawn[drawn.length - 1].i)} y={H - 8} fontSize="9" fill="var(--grey-500)" textAnchor="middle">
            {format(parseISO(points[drawn[drawn.length - 1].i].date || points[drawn[drawn.length - 1].i].scanned_on), 'd MMM')}
          </text>
        )}
      </svg>
      {drawn.length > 1 && (
        <div style={{ fontSize: '0.8rem', marginTop: 4, color: improved ? 'var(--green)' : 'var(--grey-700)', fontWeight: 600 }}>
          {delta >= 0 ? '▲' : '▼'} {round1(Math.abs(delta))}{unit} since you started
        </div>
      )}
    </div>
  );
}

// ── Strength tab ───────────────────────────────────────────────────────
function Strength() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);

  useEffect(() => {
    supabase.rpc('get_my_logged_exercises').then(({ data }) => {
      const list = data || [];
      setExercises(list);
      if (list.length) setSelected(list[0].name);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setRowsLoading(true);
    supabase.rpc('get_my_exercise_progress', { ex_name: selected }).then(({ data }) => {
      setRows(data || []);
      setRowsLoading(false);
    });
  }, [selected]);

  const points = useMemo(() => sessionPoints(rows), [rows]);

  const pbs = useMemo(() => {
    if (!rows.length) return null;
    let bestE1 = null, heaviest = null, bestVol = null;
    for (const r of rows) {
      const w = Number(r.weight), reps = Number(r.reps);
      const est = e1rm(w, reps);
      if (est != null && (!bestE1 || est > bestE1.v)) bestE1 = { v: est, w, reps, date: r.performed_on };
      if (!heaviest || w > heaviest.w || (w === heaviest.w && reps > heaviest.reps))
        heaviest = { w, reps, date: r.performed_on };
      const vol = w * reps;
      if (!bestVol || vol > bestVol.v) bestVol = { v: vol, w, reps, date: r.performed_on };
    }
    return { bestE1, heaviest, bestVol };
  }, [rows]);

  const filtered = query
    ? exercises.filter(e => e.name.toLowerCase().includes(query.toLowerCase()))
    : exercises;

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (!exercises.length) {
    return (
      <div style={{ ...card, textAlign: 'center', color: 'var(--grey-700)' }}>
        <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>🏋️</div>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>No lifts logged yet</div>
        <p style={{ fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
          Log a few training sessions from the Training tab and your strength trends will show up here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* exercise picker */}
      <div style={card}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Choose an exercise
        </div>
        <input
          style={{ ...inputStyle, marginBottom: 10 }}
          placeholder="Search your exercises…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 132, overflowY: 'auto' }}>
          {filtered.map(ex => {
            const active = ex.name === selected;
            return (
              <button
                key={ex.name}
                type="button"
                onClick={() => { setSelected(ex.name); setQuery(''); }}
                style={{
                  border: active ? '1px solid var(--green)' : '1px solid var(--grey-300)',
                  background: active ? 'var(--green-pale)' : 'var(--white, #fff)',
                  color: active ? 'var(--green)' : 'var(--grey-700)',
                  borderRadius: 999, padding: '7px 13px', fontSize: '0.82rem',
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                {ex.name}
                <span style={{ opacity: 0.6, marginLeft: 6, fontWeight: 500 }}>{ex.log_count}</span>
              </button>
            );
          })}
          {!filtered.length && <div style={{ fontSize: '0.85rem', color: 'var(--grey-500)' }}>No match.</div>}
        </div>
      </div>

      {selected && (
        <>
          {/* e1RM chart */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
              <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{selected}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>Estimated 1RM</div>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--grey-500)', margin: '0 0 8px', lineHeight: 1.4 }}>
              Your best estimated one-rep max each session — so heavy triples and light high-rep sets compare fairly.
            </p>
            {rowsLoading
              ? <div className="loading-center"><div className="spinner" /></div>
              : <LineChart points={points} accessor={p => round1(p.e1rm)} unit="kg" />}
          </div>

          {/* PB badges */}
          {pbs && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
              <Badge label="Best est. 1RM" value={pbs.bestE1 ? `${round1(pbs.bestE1.v)}kg` : '—'}
                     sub={pbs.bestE1 ? `${pbs.bestE1.w}kg × ${pbs.bestE1.reps}` : ''} />
              <Badge label="Heaviest" value={pbs.heaviest ? `${pbs.heaviest.w}kg` : '—'}
                     sub={pbs.heaviest ? `× ${pbs.heaviest.reps}` : ''} />
              <Badge label="Best set volume" value={pbs.bestVol ? `${round1(pbs.bestVol.v)}kg` : '—'}
                     sub={pbs.bestVol ? `${pbs.bestVol.w}kg × ${pbs.bestVol.reps}` : ''} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Workout volume tab ─────────────────────────────────────────────────
// Total tonnage (Σ weight × reps) for one logged session.
function sessionVolume(session) {
  const sets = (session && session.sets) || [];
  let v = 0;
  for (const s of sets) {
    if (s.weight == null || s.reps == null) continue;
    const w = Number(s.weight), reps = Number(s.reps);
    if (!Number.isNaN(w) && !Number.isNaN(reps)) v += w * reps;
  }
  return v;
}
// One point per workout, oldest-first, for the volume trend line.
function volumePoints(sessions) {
  return (sessions || [])
    .map(s => ({ date: s.performed_on, created_at: s.created_at, volume: sessionVolume(s) }))
    .filter(p => p.volume > 0)
    .sort((a, b) => (a.date === b.date
      ? String(a.created_at).localeCompare(String(b.created_at))
      : String(a.date).localeCompare(String(b.date))));
}
function kg(n) { return `${Math.round(n).toLocaleString()} kg`; }

// Stable key for one workout (a programme within a phase).
function progKey(s) {
  return String(s.programme_id != null ? s.programme_id : (s.programme_name || 'Workout'));
}

function Volume() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prog, setProg] = useState('all');

  useEffect(() => {
    supabase.rpc('get_my_training_history', { limit_count: 200 }).then(({ data }) => {
      setSessions(data || []);
      setLoading(false);
    });
  }, []);

  // Distinct workouts the member has actually logged, most-recent first.
  const programmes = useMemo(() => {
    const map = new Map();
    for (const s of sessions) {
      if (sessionVolume(s) <= 0) continue;
      const key = progKey(s);
      const label = `${s.phase_name ? s.phase_name + ' · ' : ''}${s.programme_name || 'Workout'}`;
      const prev = map.get(key);
      if (!prev) map.set(key, { key, label, count: 1, last: s.performed_on });
      else { prev.count += 1; if (String(s.performed_on) > String(prev.last)) prev.last = s.performed_on; }
    }
    return Array.from(map.values()).sort((a, b) => String(b.last).localeCompare(String(a.last)));
  }, [sessions]);

  const filtered = useMemo(
    () => (prog === 'all' ? sessions : sessions.filter(s => progKey(s) === prog)),
    [sessions, prog]
  );
  const points = useMemo(() => volumePoints(filtered), [filtered]);
  const best = useMemo(() => points.reduce((m, p) => (!m || p.volume > m.volume ? p : m), null), [points]);
  const latest = points.length ? points[points.length - 1] : null;
  const selLabel = prog === 'all'
    ? 'All workouts'
    : (programmes.find(p => p.key === prog) || {}).label || 'Workout';

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (!programmes.length) {
    return (
      <div style={{ ...card, textAlign: 'center', color: 'var(--grey-700)' }}>
        <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>📈</div>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>No workout volume yet</div>
        <p style={{ fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
          Log a few training sessions from the Training tab and your total workload will chart here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* workout picker */}
      <div style={card}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Choose a workout
        </div>
        <select value={prog} onChange={e => setProg(e.target.value)} style={inputStyle}>
          <option value="all">All workouts (whole sessions)</option>
          {programmes.map(p => (
            <option key={p.key} value={p.key}>{p.label} ({p.count})</option>
          ))}
        </select>
        {prog !== 'all' && (
          <p style={{ fontSize: '0.74rem', color: 'var(--grey-500)', margin: '8px 0 0', lineHeight: 1.4 }}>
            Showing only sessions logged against this workout, so you can see progress within the phase.
          </p>
        )}
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
          <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{selLabel}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--grey-500)' }}>total volume</div>
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--grey-500)', margin: '0 0 8px', lineHeight: 1.4 }}>
          Every set's weight × reps added up per session — your workload trending over time.
        </p>
        {points.length === 0 ? (
          <div style={{ padding: 12, fontSize: '0.85rem', color: 'var(--grey-500)' }}>No volume logged for this workout yet.</div>
        ) : points.length === 1 ? (
          <div style={{ padding: 12, fontSize: '0.85rem', color: 'var(--grey-500)' }}>
            One session so far — {kg(points[0].volume)}. Log this workout again to see the trend line.
          </div>
        ) : (
          <LineChart points={points} accessor={p => p.volume} unit="kg" />
        )}
      </div>

      {points.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
          <Badge label="Latest" value={kg(latest.volume)} sub={format(parseISO(latest.date), 'd MMM yyyy')} />
          <Badge label="Biggest" value={kg(best.volume)} sub={format(parseISO(best.date), 'd MMM yyyy')} />
        </div>
      )}
    </div>
  );
}

function Badge({ label, value, sub }) {
  return (
    <div style={{
      background: '#1A1A1A', borderRadius: 12, padding: '12px 10px', textAlign: 'center',
    }}>
      <div style={{ fontSize: '0.58rem', color: '#FF8033', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#fff', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.64rem', color: '#888', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Body / InBody tab ──────────────────────────────────────────────────
const BLANK_SCAN = {
  scanned_on: '', weight_kg: '', skeletal_muscle_kg: '', fat_mass_kg: '',
  percent_body_fat: '', visceral_fat_level: '', inbody_score: '', notes: '',
};

function Body() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK_SCAN);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function load() {
    setLoading(true);
    supabase.rpc('get_my_inbody_scans').then(({ data }) => {
      setScans(data || []);
      setLoading(false);
      // Mark any coach feedback on scans as read now the member has seen it.
      if ((data || []).some(s => s.feedback && !s.feedback.read_at)) {
        supabase.rpc('mark_my_scan_feedback_read').catch(() => {});
      }
    });
  }
  useEffect(load, []);

  // charts want oldest-first
  const asc = useMemo(() => [...scans].reverse(), [scans]);
  const latest = scans[0] || null;

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true); setErr('');
    const payload = { ...form, scanned_on: form.scanned_on || undefined };
    const { error } = await supabase.rpc('add_my_inbody_scan', { payload });
    setSaving(false);
    if (error) { setErr(error.message || 'Could not save scan.'); return; }
    setForm(BLANK_SCAN); setAdding(false); load();
  }

  async function remove(id) {
    await supabase.rpc('delete_my_inbody_scan', { p_scan_id: id });
    load();
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      {latest && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
          <Stat label="Muscle (SMM)" value={fmt(latest.skeletal_muscle_kg, 'kg')} />
          <Stat label="Body fat" value={fmt(latest.percent_body_fat, '%')} />
          <Stat label="Fat mass" value={fmt(latest.fat_mass_kg, 'kg')} />
          <Stat label="Weight" value={fmt(latest.weight_kg, 'kg')} />
          <Stat label="InBody score" value={fmt(latest.inbody_score, '')} />
        </div>
      )}

      {asc.length >= 1 && (
        <>
          <ChartCard title="Skeletal Muscle Mass" sub="Higher is better" scans={asc}
                     accessor={s => num(s.skeletal_muscle_kg)} unit="kg" color="#FF6200" />
          <ChartCard title="Percent Body Fat" sub="Lower is better" scans={asc}
                     accessor={s => num(s.percent_body_fat)} unit="%" color="#2E7D32" lowerIsBetter />
          <ChartCard title="Fat Mass" sub="Lower is better" scans={asc}
                     accessor={s => num(s.fat_mass_kg)} unit="kg" color="#EF6C00" lowerIsBetter />
          <ChartCard title="Weight" sub="" scans={asc}
                     accessor={s => num(s.weight_kg)} unit="kg" color="#1565C0" />
          <ChartCard title="Visceral Fat" sub="Lower is better" scans={asc}
                     accessor={s => num(s.visceral_fat_level)} unit="" color="#C62828" lowerIsBetter />
          <ChartCard title="InBody Score" sub="Higher is better" scans={asc}
                     accessor={s => num(s.inbody_score)} unit="" color="#6A1B9A" />
        </>
      )}

      {/* add scan */}
      {!adding ? (
        <div style={{ marginBottom: 16 }}>
          <button type="button" className="btn btn-primary btn-full" onClick={() => setAdding(true)}>
            + Add a scan
          </button>
        </div>
      ) : (
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>New InBody scan</div>
          <div style={{ fontSize: '0.74rem', lineHeight: 1.4, color: 'var(--grey-500)', marginBottom: 12 }}>
            Enter the numbers from <strong>page 1</strong> of your InBody sheet — weight, muscle, body fat, visceral fat and InBody score.
          </div>

          <Field label="Scan date">
            <input type="date" style={inputStyle} value={form.scanned_on} onChange={e => setField('scanned_on', e.target.value)} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Weight (kg)"><input type="number" inputMode="decimal" style={inputStyle} value={form.weight_kg} onChange={e => setField('weight_kg', e.target.value)} /></Field>
            <Field label="Muscle SMM (kg)"><input type="number" inputMode="decimal" style={inputStyle} value={form.skeletal_muscle_kg} onChange={e => setField('skeletal_muscle_kg', e.target.value)} /></Field>
            <Field label="Fat mass (kg)"><input type="number" inputMode="decimal" style={inputStyle} value={form.fat_mass_kg} onChange={e => setField('fat_mass_kg', e.target.value)} /></Field>
            <Field label="Body fat (%)"><input type="number" inputMode="decimal" style={inputStyle} value={form.percent_body_fat} onChange={e => setField('percent_body_fat', e.target.value)} /></Field>
            <Field label="Visceral fat"><input type="number" inputMode="decimal" style={inputStyle} value={form.visceral_fat_level} onChange={e => setField('visceral_fat_level', e.target.value)} /></Field>
            <Field label="InBody score"><input type="number" inputMode="decimal" style={inputStyle} value={form.inbody_score} onChange={e => setField('inbody_score', e.target.value)} /></Field>
          </div>
          <Field label="Notes (optional)">
            <input style={inputStyle} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="e.g. morning, post-holiday" />
          </Field>
          {err && <div style={{ color: 'var(--danger, #D32F2F)', fontSize: '0.82rem', marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setAdding(false); setForm(BLANK_SCAN); setErr(''); }}>Cancel</button>
            <button type="button" className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save scan'}</button>
          </div>
        </div>
      )}

      {/* scan list */}
      {scans.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            All scans
          </div>
          {scans.map(s => (
            <div key={s.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--grey-100)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{format(parseISO(s.scanned_on), 'd MMM yyyy')}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                    {[fmtInline(s.skeletal_muscle_kg, 'SMM', 'kg'), fmtInline(s.fat_mass_kg, 'FM', 'kg'), fmtInline(s.percent_body_fat, 'BF', '%'), fmtInline(s.weight_kg, 'Wt', 'kg')].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <button type="button" onClick={() => remove(s.id)} style={{ background: 'none', border: 'none', color: 'var(--grey-500)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
                  Remove
                </button>
              </div>
              {s.feedback && s.feedback.note && (
                <div style={{ marginTop: 8, padding: '9px 11px', background: 'var(--green-pale)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--grey-700)', lineHeight: 1.45 }}>
                  <span style={{ fontWeight: 800, color: 'var(--green)' }}>💬 Coach feedback</span>
                  <div style={{ marginTop: 3 }}>{s.feedback.note}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {scans.length === 0 && !adding && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--grey-700)' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>📊</div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Track your InBody scans</div>
          <p style={{ fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
            Add your scan numbers after each session and watch muscle climb and body fat drop over time.
          </p>
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, sub, scans, accessor, unit, color, lowerIsBetter }) {
  const hasData = scans.some(s => accessor(s) != null);
  if (!hasData) return null;
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontWeight: 800, fontSize: '1rem' }}>{title}</div>
        {sub && <div style={{ fontSize: '0.7rem', color: 'var(--grey-500)' }}>{sub}</div>}
      </div>
      <LineChart points={scans} accessor={accessor} unit={unit} color={color} lowerIsBetter={lowerIsBetter} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: 'var(--grey-100)', borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: '0.68rem', color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 900 }}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-700)', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

function num(v) { return v == null || v === '' ? null : Number(v); }
function fmt(v, unit) { return v == null ? '—' : `${round1(Number(v))}${unit}`; }
function fmtInline(v, name, unit) { return v == null ? null : `${name} ${round1(Number(v))}${unit}`; }

// ── Page ───────────────────────────────────────────────────────────────
export default function ProgressPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState('strength');

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button type="button" onClick={() => nav(-1)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1, padding: 0, color: 'var(--grey-700)' }}>‹</button>
        <div className="page-title" style={{ margin: 0 }}>My Progress</div>
      </div>

      {/* segmented control */}
      <div style={{ display: 'flex', background: 'var(--grey-100)', borderRadius: 999, padding: 4, marginBottom: 16 }}>
        {[{ k: 'strength', l: 'Strength' }, { k: 'volume', l: 'Volume' }, { k: 'body', l: 'Body scan' }].map(t => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            style={{
              flex: 1, border: 'none', borderRadius: 999, padding: '9px 0',
              fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
              background: tab === t.k ? '#fff' : 'transparent',
              color: tab === t.k ? 'var(--black)' : 'var(--grey-500)',
              boxShadow: tab === t.k ? 'var(--shadow, 0 1px 3px rgba(0,0,0,0.1))' : 'none',
            }}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'strength' ? <Strength /> : tab === 'volume' ? <Volume /> : <Body />}
    </div>
  );
}
